/**
 * Quick Stats Grid
 * Overview cards showing key metrics
 */
import type { ReactElement } from 'react';
import type { DashboardSummary } from '../hooks/useLabDashboard';

// =============================================================================
// Types
// =============================================================================

interface QuickStatsGridProps {
  summary: DashboardSummary;
}

interface StatCardProps {
  label: string;
  value: string | number;
  subValue?: string;
  variant?: 'default' | 'success' | 'danger' | 'warning';
}

// =============================================================================
// Helper Components
// =============================================================================

function StatCard({ label, value, subValue, variant = 'default' }: StatCardProps): ReactElement {
  const variantClasses = {
    default: 'text-white',
    success: 'text-theme-success',
    danger: 'text-theme-danger',
    warning: 'text-theme-warning',
  };

  return (
    <div className="bg-theme-surface/50 rounded-lg p-4">
      <div className="text-xs text-theme-text-secondary uppercase tracking-wide mb-1">{label}</div>
      <div className={`text-2xl font-bold ${variantClasses[variant]}`}>{value}</div>
      {subValue && <div className="text-xs text-theme-text-tertiary mt-1">{subValue}</div>}
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export function QuickStatsGrid({ summary }: QuickStatsGridProps): ReactElement {
  const hitRatePercent = (summary.hitRate * 100).toFixed(1);
  const hitRateVariant =
    summary.hitRate >= 0.52 ? 'success' : summary.hitRate >= 0.48 ? 'default' : 'danger';

  // Calculate ROI from hit rate (assuming -110 odds)
  // Breakeven at ~52.4% hit rate
  const settledNonPush = summary.hits + summary.misses;
  const profit = settledNonPush > 0 ? summary.hits * 90.91 - summary.misses * 100 : 0;
  const wagered = settledNonPush * 100;
  const roi = wagered > 0 ? (profit / wagered) * 100 : 0;
  const roiStr = roi >= 0 ? `+${roi.toFixed(1)}%` : `${roi.toFixed(1)}%`;
  const roiVariant = roi > 0 ? 'success' : roi < 0 ? 'danger' : 'default';

  return (
    <div className="grid grid-cols-2 @3xl:grid-cols-4 @5xl:grid-cols-6 gap-4">
      <StatCard label="Total Picks" value={summary.totalPicks.toLocaleString()} />
      <StatCard
        label="Settled"
        value={summary.settledPicks.toLocaleString()}
        subValue={`${summary.pushes} pushes`}
      />
      <StatCard label="Record" value={`${summary.hits}-${summary.misses}`} />
      <StatCard label="Hit Rate" value={`${hitRatePercent}%`} variant={hitRateVariant} />
      <StatCard label="Est. ROI" value={roiStr} variant={roiVariant} subValue="@ -110 odds" />
      {summary.bestConfig && (
        <StatCard
          label="Best Config"
          value={summary.bestConfig}
          subValue={`${(summary.bestConfigHitRate * 100).toFixed(1)}% hit rate`}
          variant="success"
        />
      )}
    </div>
  );
}
